import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  LinearProgress,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Switch,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import PersistentDialog from "../../components/common/PersistentDialog";
import {
  Add,
  Edit,
  Delete,
  Search,
  Visibility,
  Close,
  Download,
  Upload,
  MenuBook,
  CloudUpload,
  CheckCircle,
  Error,
  Description,
  FileDownload,
  AccessTime,
} from "@mui/icons-material";
import DataImportWizard from "../../components/common/DataImportWizard";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAllSubjects } from "../../hooks/queries/useCourses";
import api from "../../services/api";
import courseService from "../../services/courseService";
import PermissionGate from "../../components/common/PermissionGate";
import { getClassDisplayLabel } from "../../utils/classDisplay";
import { canToggleEntityStatus, getEntityStatusToggleTooltip } from "../../utils/statusToggle";
import DepartmentModuleFilterSelect from "../../components/common/DepartmentModuleFilterSelect";
import { useDepartmentModuleFilter } from "../../hooks/useDepartmentModuleFilter";

const Subjects = () => {
  const { selectedInstitute } = useSelector((state) => state.instituteContext || {});
  const {
    departmentFilter,
    setDepartmentFilter,
    showAllDepartmentsOption,
    departmentApiParams,
    effectiveInstituteId,
  } = useDepartmentModuleFilter();
  // Get current user from localStorage
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  });
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [courseFilter, setCourseFilter] = useState("all");
  const [academicYearFilter, setAcademicYearFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const EMPTY_SUBJECT_FORM = {
    name: "",
    code: "",
    course: "",
    semester: "",
    credits: "",
    weeklyHours: "3",
    totalHours: "",
    assignments: [{ classId: "", teacher: "" }],
  };

  const [formData, setFormData] = useState({ ...EMPTY_SUBJECT_FORM });

  // Import states
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  // Search text for the assignment Teacher dropdown (filters the option list).
  const [teacherSearch, setTeacherSearch] = useState("");
  const [courseClasses, setCourseClasses] = useState([]);
  const [courseClassesLoading, setCourseClassesLoading] = useState(false);
  const [classSectionMap, setClassSectionMap] = useState({}); // id -> section from get-all-classes (for display only)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewSubject, setViewSubject] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  
  const [courseFilterOptions, setCourseFilterOptions] = useState([]);
  const [courseFilterLoading, setCourseFilterLoading] = useState(false);
  const [batchOptions, setBatchOptions] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  
  const [classFilterOptions, setClassFilterOptions] = useState([]);
  const [classFilterLoading, setClassFilterLoading] = useState(false);
  const [existingAssignments, setExistingAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [teacherPopoverAnchor, setTeacherPopoverAnchor] = useState(null);
  const [teacherPopoverData, setTeacherPopoverData] = useState([]);
  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [classDialogData, setClassDialogData] = useState([]);

  const handleTeacherPopoverOpen = (event, assignments) => {
    event.stopPropagation();
    setTeacherPopoverAnchor(event.currentTarget);
    setTeacherPopoverData(assignments);
  };

  const handleTeacherPopoverClose = () => {
    setTeacherPopoverAnchor(null);
  };

  const handleClassDialogOpen = (classes) => {
    setClassDialogData(Array.isArray(classes) ? classes : []);
    setClassDialogOpen(true);
  };

  const handleClassDialogClose = () => {
    setClassDialogOpen(false);
    setClassDialogData([]);
  };

  const { data: courseNamesResponse, isLoading: coursesLoading, refetch: refetchCourseNames } = useQuery({
    queryKey: ["course-names", currentUser?.instituteId],
    queryFn: courseService.getCourseNames,
    enabled: createDialogOpen,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const { data: semestersResponse, isLoading: semestersLoading } = useQuery({
    queryKey: ["academic-info-semesters", currentUser?.instituteId],
    queryFn: async () => {
      const params = new URLSearchParams({ page: "1", limit: "200" });
      if (currentUser?.instituteId) params.set("instituteId", String(currentUser.instituteId));
      const res = await api.get(`/academic-info/semester/all?${params.toString()}`);
      const data = res?.data?.data ?? res?.data ?? {};
      return data?.semesters ?? [];
    },
    enabled: createDialogOpen,
    // Semesters can be added in Academic Info from another screen.
    // Refetch on focus/mount so user doesn't need a hard refresh.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: "always",
  });

  // When create dialog opens, force-refresh semesters (Academic Info can change them)
  useEffect(() => {
    if (!createDialogOpen) return;
    const instId = currentUser?.instituteId;
    if (!instId) return;
    queryClient.invalidateQueries({ queryKey: ["academic-info-semesters", instId] });
    queryClient.refetchQueries({ queryKey: ["academic-info-semesters", instId] });
  }, [createDialogOpen, currentUser?.instituteId, queryClient]);

  const semestersForDropdown = Array.isArray(semestersResponse) ? semestersResponse : [];

  const courseOptions = Array.isArray(courseNamesResponse?.data)
    ? courseNamesResponse.data
    : Array.isArray(courseNamesResponse)
      ? courseNamesResponse
      : Array.isArray(courseNamesResponse?.data?.data)
        ? courseNamesResponse.data.data
        : [];

  const fetchTeachersList = async (courseId) => {
    if (!courseId) return [];
    const response = await api.get(`/course/course-teachers`, { params: { id: courseId } });
    return Array.isArray(response?.data?.data)
      ? response.data.data
      : Array.isArray(response?.data)
        ? response.data
        : [];
  };

  const enrichClassSectionMap = async () => {
    try {
      const allRes = await api.get("/class/get-all-classes", {
        params: { instituteId: currentUser?.instituteId ?? selectedInstitute?.id },
      });
      const allList = Array.isArray(allRes?.data?.data)
        ? allRes.data.data
        : Array.isArray(allRes?.data)
          ? allRes.data
          : [];
      const map = {};
      allList.forEach((cls) => {
        const raw = cls?.class ?? cls?.Class ?? cls;
        const id = cls.id ?? cls.classId ?? raw?.id ?? raw?.classId;
        const section = raw?.section ?? cls.section ?? raw?.sectionName ?? cls.sectionName ?? "";
        if (id != null) map[String(id)] = section;
      });
      setClassSectionMap(map);
    } catch (_) {
      setClassSectionMap({});
    }
  };

  const fetchCourseClassesList = async (courseId) => {
    if (!courseId) return [];
    const response = await api.get("/course/course-classes", { params: { id: courseId } });
    return Array.isArray(response?.data?.data)
      ? response.data.data
      : Array.isArray(response?.data)
        ? response.data
        : [];
  };

  const buildTeachersFromRow = (row, formAssignments = []) => {
    const teachers = [];
    const seen = new Set();
    const addTeacher = (teacherId, teacherName) => {
      const tid = teacherId != null ? String(teacherId) : "";
      if (!tid || seen.has(tid)) return;
      seen.add(tid);
      teachers.push({
        id: Number(teacherId),
        teacherId: Number(teacherId),
        fullName: teacherName || `Teacher ${teacherId}`,
      });
    };

    (Array.isArray(formAssignments) ? formAssignments : []).forEach((a) => {
      const tid = a.teacher ?? a.teacherId;
      const detail = row?.assignments?.find(
        (x) => String(x.teacherId ?? x.teacher_id ?? x.teacher) === String(tid)
      );
      addTeacher(tid, detail?.teacherName || row?.teacherName || row?.teacher);
    });
    if (row?.teacherId != null) {
      addTeacher(row.teacherId, row.teacherName || row.teacher);
    }
    return teachers;
  };

  const mergeTeachersFromApi = (teacherList, row, formAssignments = []) => {
    const merged = Array.isArray(teacherList) ? [...teacherList] : [];
    const ids = new Set(merged.map((t) => String(t.id ?? t.teacherId)));
    buildTeachersFromRow(row, formAssignments).forEach((t) => {
      const tid = String(t.id ?? t.teacherId);
      if (!ids.has(tid)) {
        ids.add(tid);
        merged.push(t);
      }
    });
    return merged;
  };

  const fetchTeachers = async (courseId) => {
    if (!courseId) {
      setTeachers([]);
      return [];
    }
    setTeachersLoading(true);
    try {
      const list = await fetchTeachersList(courseId);
      setTeachers(list);
      return list;
    } catch (e) {
      setTeachers([]);
      return [];
    } finally {
      setTeachersLoading(false);
    }
  };

  const mergeAssignedClassesIntoPicker = (classList, row, formAssignments = []) => {
    const merged = Array.isArray(classList) ? [...classList] : [];
    const ids = new Set(merged.map((c) => String(c.id ?? c.classId)));

    const addClass = (cls) => {
      const id = cls?.id ?? cls?.classId;
      if (id == null || ids.has(String(id))) return;
      ids.add(String(id));
      merged.push({
        id: Number(id),
        name: cls?.name ?? cls?.className ?? `Class ${id}`,
        section: cls?.section ?? cls?.sectionName ?? null,
      });
    };

    (Array.isArray(row?.classes) ? row.classes : []).forEach(addClass);
    (Array.isArray(row?.assignments) ? row.assignments : []).forEach((a) => {
      if (a?.class) addClass(a.class);
      else if (a?.classId != null) {
        addClass({
          id: a.classId,
          name: a.className || a.class?.name,
          section: a.section ?? a.class?.section,
        });
      }
    });
    (Array.isArray(formAssignments) ? formAssignments : []).forEach((a) => {
      if (!a?.classId || ids.has(String(a.classId))) return;
      const detail = row?.assignments?.find((x) => String(x.classId ?? x.class_id) === String(a.classId));
      const fromClasses = row?.classes?.find((x) => String(x.id) === String(a.classId));
      addClass({
        id: a.classId,
        name: detail?.className || detail?.class?.name || fromClasses?.name,
        section: detail?.section ?? detail?.class?.section ?? fromClasses?.section ?? classSectionMap[String(a.classId)],
      });
    });

    return merged;
  };

  // Fetch classes for selected course from course-classes (correct list). Enrich section from get-all-classes for display only.
  const fetchCourseClasses = async (courseId) => {
    if (!courseId) {
      setCourseClasses([]);
      return [];
    }
    setCourseClassesLoading(true);
    try {
      const list = await fetchCourseClassesList(courseId);
      setCourseClasses(list);
      await enrichClassSectionMap();
      return list;
    } catch (e) {
      setCourseClasses([]);
      return [];
    } finally {
      setCourseClassesLoading(false);
    }
  };

  const loadEditSubjectDropdowns = async (courseId, row, formAssignments) => {
    setCourseClassesLoading(true);
    setTeachersLoading(true);
    try {
      const [classList, teacherList] = await Promise.all([
        fetchCourseClassesList(courseId),
        fetchTeachersList(courseId),
      ]);
      setCourseClasses(mergeAssignedClassesIntoPicker(classList, row, formAssignments));
      setTeachers(mergeTeachersFromApi(teacherList, row, formAssignments));
      await enrichClassSectionMap();
    } catch (_) {
      // Keep row-prefilled options visible if refresh fails
    } finally {
      setCourseClassesLoading(false);
      setTeachersLoading(false);
    }
  };

  const getClassOptionLabel = (c) => {
    const raw = c?.class ?? c?.Class ?? c;
    const name = raw.name ?? c.name ?? raw.className ?? c.className ?? c.label ?? "";
    const sectionFromApi =
      raw.section ??
      c.section ??
      raw.sectionName ??
      c.sectionName ??
      raw.section_name ??
      c.section_name ??
      raw.Section ??
      c.Section ??
      "";
    const section = sectionFromApi || classSectionMap[String(c.id ?? c.classId)] || "";
    const sectionDisplay = section
      ? String(section).charAt(0).toUpperCase() + String(section).slice(1).toLowerCase()
      : "";
    return section ? `${name} - ${sectionDisplay}` : name || `Class ${c.id ?? c.classId}`;
  };

  const renderClassSelectValue = (selected) => {
    if (!selected) return "";
    const match = courseClasses.find((c) => String(c.id ?? c.classId) === String(selected));
    if (match) return getClassOptionLabel(match);
    return courseClassesLoading ? "Loading classes..." : selected;
  };

  const renderTeacherSelectValue = (selected) => {
    if (!selected) return "";
    const match = teachers.find((t) => String(t.id ?? t.teacherId) === String(selected));
    if (match) {
      return match.fullName || `${match.firstName || ""} ${match.lastName || ""}`.trim() || match.name || `Teacher ${selected}`;
    }
    return teachersLoading ? "Loading teachers..." : selected;
  };

  // Consistent display name for a teacher row (used for both rendering and sorting/searching).
  const getTeacherDisplayName = (t) =>
    (t?.fullName ||
      `${t?.firstName || ""} ${t?.lastName || ""}`.trim() ||
      t?.name ||
      (t?.id != null ? `Teacher ${t.id}` : "")).trim();

  // Teacher options for the assignment dropdown: sorted A→Z by name, then filtered by the
  // in-menu search box (case-insensitive). Memoized so typing does not re-sort the whole list
  // on every keystroke unnecessarily.
  const sortedTeachers = useMemo(() => {
    const list = Array.isArray(teachers) ? [...teachers] : [];
    return list.sort((a, b) =>
      getTeacherDisplayName(a).localeCompare(getTeacherDisplayName(b), undefined, {
        sensitivity: "base",
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teachers]);

  const filteredTeachers = useMemo(() => {
    const q = teacherSearch.trim().toLowerCase();
    if (!q) return sortedTeachers;
    return sortedTeachers.filter((t) =>
      getTeacherDisplayName(t).toLowerCase().includes(q)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedTeachers, teacherSearch]);

  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    setFormData({ ...formData, course: courseId, assignments: [{ classId: "", teacher: "" }] });
    fetchCourseClasses(courseId);
    fetchTeachers(courseId);
  };

  const handleAddAssignment = () => {
    setFormData((prev) => ({
      ...prev,
      assignments: [...prev.assignments, { classId: "", teacher: "" }],
    }));
  };

  const handleRemoveAssignment = (index) => {
    setFormData((prev) => ({
      ...prev,
      assignments: prev.assignments.filter((_, i) => i !== index),
    }));
  };

  const handleAssignmentChange = (index, field, value) => {
    setFormData((prev) => {
      const newAssignments = [...prev.assignments];
      newAssignments[index] = { ...newAssignments[index], [field]: value };
      return { ...prev, assignments: newAssignments };
    });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const openViewSubject = (row) => {
    setViewSubject(row);
    setViewDialogOpen(true);
  };

  const openAssignDialog = async (subject) => {
    setSelectedSubject(subject);
    setAssignDialogOpen(true);
    setEditingAssignment(null);

    // Fetch classes for assignment
    setClassesLoading(true);
    try {
      const response = await api.get("/class/get-all-classes", {
        params: { instituteId: currentUser?.instituteId }
      });
      const classesList = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setClasses(classesList);
    } catch (error) {
      console.error("Failed to fetch classes:", error);
      setClasses([]);
    } finally {
      setClassesLoading(false);
    }

    // Fetch existing assignments for this subject
    setAssignmentsLoading(true);
    try {
      const assignmentsResponse = await api.get("/class-subjects", {
        params: { subjectId: subject.id, instituteId: currentUser?.instituteId }
      });
      const assignmentsList = Array.isArray(assignmentsResponse?.data?.data)
        ? assignmentsResponse.data.data
        : Array.isArray(assignmentsResponse?.data)
          ? assignmentsResponse.data
          : [];
      setExistingAssignments(assignmentsList);
    } catch (error) {
      console.error("Failed to fetch existing assignments:", error);
      setExistingAssignments([]);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
  };

  const handleCancelEdit = () => {
    setEditingAssignment(null);
  };

  const handleSaveAssignment = async (assignmentData) => {
    if (!selectedSubject || !assignmentData.classId) return;

    try {
      const payload = {
        classId: parseInt(assignmentData.classId),
        subjectId: selectedSubject.id,
        hoursPerWeek: assignmentData.hoursPerWeek || 3,
        isCompulsory: assignmentData.isCompulsory !== undefined ? assignmentData.isCompulsory : true,
        academicYear: assignmentData.academicYear || `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
        preferredTeacherId: assignmentData.preferredTeacherId || null,
        isActive: assignmentData.isActive !== undefined ? assignmentData.isActive : true,
        preferredTimeSlots: assignmentData.preferredTimeSlots || null
      };

      if (assignmentData.id) {
        // Update existing assignment
        await api.patch(`/class-subjects/${assignmentData.id}`, payload);
        setSnackbar({
          open: true,
          message: "Assignment updated successfully!",
          severity: "success"
        });
      } else {
        // Create new assignment
        await api.post("/class-subjects", payload);
        setSnackbar({
          open: true,
          message: "Subject assigned to class successfully!",
          severity: "success"
        });
      }

      // Refresh assignments
      const assignmentsResponse = await api.get("/class-subjects", {
        params: { subjectId: selectedSubject.id, instituteId: currentUser?.instituteId }
      });
      const assignmentsList = Array.isArray(assignmentsResponse?.data?.data)
        ? assignmentsResponse.data.data
        : Array.isArray(assignmentsResponse?.data)
          ? assignmentsResponse.data
          : [];
      setExistingAssignments(assignmentsList);

      setEditingAssignment(null);
    } catch (error) {
      console.error("Failed to save assignment:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to save assignment",
        severity: "error"
      });
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    try {
      await api.delete(`/class-subjects/${assignmentId}`);
      setSnackbar({
        open: true,
        message: "Assignment deleted successfully!",
        severity: "success"
      });

      // Refresh assignments
      const assignmentsResponse = await api.get("/class-subjects", {
        params: { subjectId: selectedSubject.id, instituteId: currentUser?.instituteId }
      });
      const assignmentsList = Array.isArray(assignmentsResponse?.data?.data)
        ? assignmentsResponse.data.data
        : Array.isArray(assignmentsResponse?.data)
          ? assignmentsResponse.data
          : [];
      setExistingAssignments(assignmentsList);
    } catch (error) {
      console.error("Error deleting assignment:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to delete assignment",
        severity: "error"
      });
    }
  };

  // Server-side pagination for subjects list
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // Reset dependent filters when department changes
  useEffect(() => {
    setCourseFilter("all");
    setAcademicYearFilter("all");
    setClassFilter("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [departmentFilter]);

  // Reset class when course changes
  useEffect(() => {
    setClassFilter("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [courseFilter]);

  // Reset pagination on search or batch change
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchQuery, academicYearFilter]);

  // Keep a global classId -> section map for view modal displays (not just course-class picker)
  useEffect(() => {
    let alive = true;
    const instituteId = currentUser?.instituteId ?? selectedInstitute?.id;
    if (!instituteId) return () => {};
    (async () => {
      try {
        const allRes = await api.get("/class/get-all-classes", { params: { instituteId } });
        const allList = Array.isArray(allRes?.data?.data)
          ? allRes.data.data
          : Array.isArray(allRes?.data)
            ? allRes.data
            : [];
        const map = {};
        allList.forEach((cls) => {
          const raw = cls?.class ?? cls?.Class ?? cls;
          const id = raw?.id ?? cls?.id ?? raw?.classId ?? cls?.classId;
          const section =
            raw?.section ??
            cls.section ??
            raw?.sectionName ??
            cls.sectionName ??
            raw?.section_name ??
            cls.section_name ??
            "";
          if (id != null && section) map[String(id)] = section;
        });
        if (alive) setClassSectionMap(map);
      } catch {
        if (alive) setClassSectionMap({});
      }
    })();
    return () => {
      alive = false;
    };
  }, [currentUser?.instituteId, selectedInstitute?.id]);

  // Fetch Batch (Academic Year) Options
  useEffect(() => {
    const fetchBatchOptions = async () => {
      setBatchLoading(true);
      try {
        const params = new URLSearchParams({ page: "1", limit: "100" });
        if (currentUser?.instituteId) params.set("instituteId", String(currentUser.instituteId));
        const res = await api.get(`/academic-info/all?${params.toString()}`);
        const data = res?.data?.data ?? res?.data ?? {};
        setBatchOptions(data?.academicYears ?? []);
      } catch (err) {
        console.error("Failed to fetch academic years", err);
        setBatchOptions([]);
      } finally {
        setBatchLoading(false);
      }
    };
    fetchBatchOptions();
  }, [currentUser?.instituteId]);

  // Fetch Course Filter Options (optionally filtered by department)
  useEffect(() => {
    const fetchCourseFilters = async () => {
      setCourseFilterLoading(true);
      try {
        const depId = departmentApiParams.departmentId;
        const response = depId
          ? await courseService.getCoursesByDepartment(depId)
          : await courseService.getCourseNames();
        const envelope = response?.data ?? response;
        const list = Array.isArray(envelope?.data)
          ? envelope.data
          : Array.isArray(envelope)
            ? envelope
            : [];
        setCourseFilterOptions(list);
      } catch (_) {
        setCourseFilterOptions([]);
      } finally {
        setCourseFilterLoading(false);
      }
    };
    fetchCourseFilters();
  }, [currentUser?.instituteId, selectedInstitute?.id, departmentApiParams.departmentId]);

  // Build params only with meaningful filters
  const subjectQueryParams = useMemo(() => {
    const params = {
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      sort: 'createdAt',
      order: 'DESC',
    };
    const trimmed = (searchQuery || '').trim();
    if (trimmed) params.search = trimmed;
    const instId = currentUser?.instituteId ?? selectedInstitute?.id;
    if (instId) params.instituteId = instId;
    if (departmentApiParams.departmentId) {
      params.departmentId = departmentApiParams.departmentId;
    }
    if (courseFilter && courseFilter !== "all") params.courseId = courseFilter;
    if (academicYearFilter && academicYearFilter !== "all") params.academicYear = academicYearFilter;
    if (classFilter) params.classId = classFilter;
    return params;
  }, [paginationModel.page, paginationModel.pageSize, searchQuery, currentUser?.instituteId, selectedInstitute?.id, departmentApiParams.departmentId, courseFilter, academicYearFilter, classFilter]);

  useEffect(() => {
    const fetchClassFilters = async () => {
      setClassFilterLoading(true);
      try {
        const params = { instituteId: currentUser?.instituteId ?? selectedInstitute?.id };
        if (departmentApiParams.departmentId) params.departmentId = departmentApiParams.departmentId;
        if (courseFilter && courseFilter !== "all") params.courseId = courseFilter;
        
        const response = await api.get("/class/get-all-classes", { params });
        const classesList = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setClassFilterOptions(classesList);
      } catch (_) {
        setClassFilterOptions([]);
      } finally {
        setClassFilterLoading(false);
      }
    };
    fetchClassFilters();
  }, [currentUser?.instituteId, selectedInstitute?.id, departmentApiParams.departmentId, courseFilter]);

  // Fetch subjects using TanStack Query and services layer
  const {
    data: subjectsResponse,
    isLoading: subjectsLoading,
    isFetching: subjectsFetching,
    isError: subjectsError,
    error: subjectsErrorObj,
  } = useAllSubjects(subjectQueryParams, {
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    keepPreviousData: true,
  });

  const _root = subjectsResponse?.data ?? subjectsResponse;
  const paginationInfo = _root?.pagination
    || _root?.data?.pagination
    || _root?.data?.data?.pagination
    || _root?.meta
    || _root?.data?.meta
    || _root?.data?.data?.meta
    || {
    currentPage: paginationModel.page + 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: paginationModel.pageSize,
  };

  const subjectsRaw = Array.isArray(_root?.subjects)
    ? _root.subjects
    : Array.isArray(_root?.data?.subjects)
      ? _root.data.subjects
      : Array.isArray(_root?.data?.data?.subjects)
        ? _root.data.data.subjects
        : Array.isArray(_root?.rows)
          ? _root.rows
          : Array.isArray(_root?.data?.rows)
            ? _root.data.rows
            : Array.isArray(_root?.data?.data?.rows)
              ? _root.data.data.rows
              : Array.isArray(_root?.items)
                ? _root.items
                : Array.isArray(_root?.data?.items)
                  ? _root.data.items
                  : Array.isArray(_root?.data?.data?.items)
                    ? _root.data.data.items
                    : Array.isArray(_root?.result)
                      ? _root.result
                      : Array.isArray(_root?.data?.result)
                        ? _root.data.result
                        : Array.isArray(_root?.data?.data?.result)
                          ? _root.data.data.result
                          : Array.isArray(_root?.content)
                            ? _root.content
                            : Array.isArray(_root?.data?.content)
                              ? _root.data.content
                              : Array.isArray(_root?.data?.data?.content)
                                ? _root.data.data.content
                                : Array.isArray(_root?.data)
                                  ? _root.data
                                  : Array.isArray(_root)
                                    ? _root
                                    : [];

  const rows = subjectsRaw.map((s, index) => {
    const teacherEntity = s?.Teacher ?? s?.teacher ?? s?.teacherDetails ?? s?.teacher_detail ?? s?.teacher_info ?? s?.teachers?.[0] ?? null;
    const departmentEntity = s?.Department ?? s?.department ?? s?.departmentDetails ?? s?.department_info ?? null;

    const id = s?.id ?? s?.subjectId ?? s?.subject_id ?? s?.uuid ?? s?._id ?? `${s?.code || s?.subjectCode || s?.subject_code || s?.name || s?.subjectName || s?.subject_name || 'subject'}-${index}`;
    const name = s?.name ?? s?.subjectName ?? s?.subject_name ?? s?.title ?? '';
    const code = s?.code ?? s?.subjectCode ?? s?.subject_code ?? s?.shortCode ?? '';
    const courseId = s?.courseId ?? s?.course_id ?? s?.Course?.id ?? s?.course?.id ?? null;
    const classesFromApi = Array.isArray(s?.classes) ? s.classes : [];
    const classEntity =
      s?.CourseClass ??
      s?.courseClass ??
      s?.Class ??
      s?.class ??
      classesFromApi[0] ??
      null;
    const classId =
      s?.classId ??
      s?.class_id ??
      classEntity?.id ??
      classesFromApi[0]?.id ??
      s?.courseClassId ??
      s?.course_class_id ??
      null;
    const className = (typeof classEntity === 'string')
      ? classEntity
      : (classEntity?.name ?? classEntity?.className ?? s?.className ?? s?.class_name ?? '');
    const teacherId = s?.teacherId ?? s?.teacher_id ?? (typeof teacherEntity === 'object' && teacherEntity ? (teacherEntity?.id ?? teacherEntity?.teacherId ?? teacherEntity?.teacher_id) : null);
    const semester = s?.semester ?? s?.sem ?? s?.semester_number ?? s?.semester_no ?? s?.semesterName ?? s?.name ?? null;
    const semesterId = s?.semesterId ?? s?.semester_id ?? null;
    const credits = s?.credits ?? s?.credit ?? s?.credit_points ?? s?.credit_hours ?? null;
    const weeklyHours = s?.weeklyHours ?? s?.hoursPerWeek ?? null;
    const totalHours = s?.totalHours ?? null;
    const completedHours = s?.completedHours ?? null;
    const remainingHours = s?.remainingHours ?? null;
    const status = (typeof s?.status === 'boolean')
      ? s.status
      : (typeof s?.isActive === 'boolean')
        ? s.isActive
        : (typeof s?.is_active === 'boolean')
          ? s.is_active
          : (s?.isDeleted === true || s?.is_deleted === true || s?.deleted === true || s?.active === false)
            ? false
            : true;

    // Handle multiple assignments if they exist
    const rawAssignments = Array.isArray(s?.assignments) ? s.assignments : [];
    const assignedTeachers = s?.assignedTeachers || ""; // Pre-formatted string from backend

    const sectionByClassId =
      (Array.isArray(classFilterOptions) ? classFilterOptions : [])
        .reduce((acc, cls) => {
          const raw = cls?.class ?? cls?.Class ?? cls;
          const id = raw?.id ?? cls?.id ?? raw?.classId ?? cls?.classId;
          const section = raw?.section ?? cls?.section ?? raw?.sectionName ?? cls?.sectionName ?? raw?.section_name ?? cls?.section_name ?? "";
          if (id != null && section) acc[String(id)] = section;
          return acc;
        }, {});
    classesFromApi.forEach((cls) => {
      if (cls?.id != null && cls?.section) sectionByClassId[String(cls.id)] = cls.section;
    });
    if (classId != null && classSectionMap[String(classId)] && !sectionByClassId[String(classId)]) {
      sectionByClassId[String(classId)] = classSectionMap[String(classId)];
    }

    // Detailed mappings for tooltip/expansion/view (include section from API + class list fallback)
    const assignments = rawAssignments.map((a) => {
      const classId = a.classId ?? a.class_id ?? null;
      const clsEntity = a.classDetails || a.class || a.Class || null;
      const sectionFromApi =
        a.section ??
        a.sectionName ??
        a.section_name ??
        clsEntity?.section ??
        (classId != null ? sectionByClassId[String(classId)] : "") ??
        "";
      const classDisplayLabel = getClassDisplayLabel({
        name: a.className || clsEntity?.name || s?.className || "",
        section: sectionFromApi,
      });
      return {
        ...a,
        classId,
        teacherId: a.teacherId ?? a.teacher_id,
        teacherName: a.teacherName || s?.teacherName || "",
        section: sectionFromApi,
        className: a.className || clsEntity?.name || "",
        classDisplayLabel,
      };
    });

    const teacherName =
      assignedTeachers ||
      (typeof teacherEntity === 'string' ? teacherEntity : (teacherEntity?.user?.fullName || teacherEntity?.fullName || s?.teacherName || ""));
    const classNames = assignments.map((a) => a.classDisplayLabel).filter(Boolean);

    const departmentName = (typeof departmentEntity === 'string')
      ? departmentEntity
      : (departmentEntity?.name || departmentEntity?.deptName || departmentEntity?.departmentName || departmentEntity?.department_name || s?.departmentName || s?.department_name || s?.deptName || '');

    return {
      id,
      subjectId: s?.id ?? s?.subjectId ?? s?.subject_id ?? null,
      name,
      code,
      courseId,
      classId,
      className: (() => {
        if (classNames.length > 0) return classNames.join(", ");
        const section = classId != null ? sectionByClassId[String(classId)] : "";
        return getClassDisplayLabel({ name: className, section });
      })(),
      teacherId,
      semester,
      semesterId,
      credits,
      weeklyHours,
      totalHours,
      completedHours,
      remainingHours,
      status,
      canDeactivate: s?.canDeactivate !== false,
      deactivationBlockers: Array.isArray(s?.deactivationBlockers) ? s.deactivationBlockers : [],
      department: departmentName,
      teacher: teacherName,
      teachers: (() => {
        const fromAssignments = assignments.map((a) => a.teacherName).filter(Boolean);
        return fromAssignments.length ? fromAssignments : (teacherName ? [teacherName] : []);
      })(),
      assignments,
      classes: classesFromApi,
      Department: departmentEntity || null,
      Teacher: teacherEntity || null,
    };
  });

  const totalItems = paginationInfo?.totalItems
    ?? _root?.total
    ?? _root?.data?.total
    ?? _root?.data?.data?.total
    ?? _root?.count
    ?? _root?.data?.count
    ?? _root?.data?.data?.count
    ?? rows.length;

  // Handle export functionality
  const handleExport = async () => {
    try {
      const path = "/export/subjects-csv";
      const response = await api.post(path);

      if (response.status !== 200) {
        throw new Error('Export failed');
      }

      // Handle CSV data from response
      const csvData = response.data.csvData;
      if (!csvData) {
        throw new Error('No CSV data received');
      }

      // Create blob from CSV string
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const fileUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = 'subjects.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(fileUrl);
      document.body.removeChild(a);

      // Show success message
      setSnackbar({
        open: true,
        message: `Export successful! ${response.data.totalRecords || 0} subjects exported.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Export error:", error);
      setSnackbar({
        open: true,
        message: "Failed to export subjects. Please try again.",
        severity: "error",
      });
    }
  };

  const openEditSubject = (row) => {
    const subjId = row?.subjectId || row?.id;
    const numericId = subjId != null ? Number(subjId) : null;
    setIsEditMode(true);
    setEditingSubjectId(numericId);
    setFieldErrors({});
    const courseId = row?.courseId ? String(row.courseId) : "";
    const classId = row?.classId != null ? String(row.classId) : (row?.courseClassId != null ? String(row.courseClassId) : "");
    const teacherId = row?.teacherId ? String(row.teacherId) : "";

    // If the backend already returns assignments array, use it. Otherwise, create one from current fields.
    const assignments = (Array.isArray(row?.assignments) && row.assignments.length > 0)
      ? row.assignments.map(a => ({
        classId: a.classId != null ? String(a.classId) : "",
        teacher: a.teacherId != null ? String(a.teacherId) : (a.teacher != null ? String(a.teacher) : "")
      }))
      : (classId || teacherId)
        ? [{ classId: classId, teacher: teacherId }]
        : [];

    setFormData({
      name: row?.name || "",
      code: row?.code || "",
      course: courseId,
      semester: row?.semesterId != null ? String(row.semesterId) : (row?.semester ? String(row.semester) : ""),
      credits: row?.credits != null ? String(row.credits) : "",
      weeklyHours:
        row?.weeklyHours != null
          ? String(row.weeklyHours)
          : row?.hoursPerWeek != null
            ? String(row.hoursPerWeek)
            : "3",
      totalHours: row?.totalHours != null ? String(row.totalHours) : "",
      assignments: assignments,
    });

    if (row?.courseId) {
      setCourseClasses(mergeAssignedClassesIntoPicker([], row, assignments));
      setTeachers(buildTeachersFromRow(row, assignments));
      setCourseClassesLoading(true);
      setTeachersLoading(true);
      loadEditSubjectDropdowns(row.courseId, row, assignments);
    } else {
      setCourseClasses([]);
      setTeachers([]);
      setCourseClassesLoading(false);
      setTeachersLoading(false);
    }

    setCreateDialogOpen(true);
  };

  const openCreateSubject = () => {
    setIsEditMode(false);
    setEditingSubjectId(null);
    setFieldErrors({});
    setFormData({ ...EMPTY_SUBJECT_FORM, assignments: [{ classId: "", teacher: "" }] });
    setCourseClasses([]);
    setTeachers([]);
    setClassSectionMap({});
    queryClient.invalidateQueries({ queryKey: ["course-names"] });
    setCreateDialogOpen(true);
  };

  const handleDialogClose = () => {
    setCreateDialogOpen(false);
    setIsEditMode(false);
    setEditingSubjectId(null);
    setFieldErrors({});
    setCourseClassesLoading(false);
    setTeachersLoading(false);
  };

  // Resolve semester for API: send exact full label as shown in dropdown (e.g. "Semester 1 (2026-2027)")
  const getSemesterPayloadValue = () => {
    if (!formData.semester) return undefined;
    const selected = semestersForDropdown.find((s) => String(s.id) === String(formData.semester));
    if (!selected) return String(formData.semester);
    const name = selected.semesterName ?? selected.name ?? String(selected.id);
    const yearPart = selected.academicYear?.academicYear ?? selected.academicYear;
    const fullLabel = yearPart ? `${name} (${yearPart})` : name;
    return fullLabel;
  };

  // Block number keys in Subject Name (letters and spaces only)
  const blockNumberKeyDown = (e) => {
    if (/^[0-9]$/.test(e.key)) e.preventDefault();
  };
  const handleSubjectNameChange = (e) => {
    const value = e.target.value.replace(/\d/g, "").slice(0, 75);
    setFormData((prev) => ({ ...prev, name: value }));
  };

  const validateForm = () => {
    const errors = {};
    const nameTrimmed = formData.name.trim();
    if (!nameTrimmed) {
      errors.name = 'Subject name is required';
    } else if (nameTrimmed.length < 2) {
      errors.name = 'Subject name must be at least 2 characters long';
    } else if (nameTrimmed.length > 75) {
      errors.name = 'Subject name cannot exceed 75 characters';
    }

    const codeTrimmed = formData.code.trim();
    if (!codeTrimmed) {
      errors.code = 'Code is required';
    } else if (codeTrimmed.length < 2) {
      errors.code = 'Code must be at least 2 characters long';
    } else if (codeTrimmed.length > 75) {
      errors.code = 'Code cannot exceed 75 characters';
    }

    if (!formData.course) {
      errors.course = "Course is required";
    }
    if (!formData.semester) {
      errors.semester = "Semester is required";
    }
    const credits = Number(formData.credits);
    if (formData.credits === '' || formData.credits === null || Number.isNaN(credits)) {
      errors.credits = 'Credits must be a number';
    } else if (credits < 1) {
      errors.credits = 'Credits must be at least 1';
    } else if (credits > 20) {
      errors.credits = 'Credits cannot exceed 20';
    }

    const weeklyHours = Number(formData.weeklyHours);
    if (formData.weeklyHours === '' || formData.weeklyHours === null || Number.isNaN(weeklyHours)) {
      errors.weeklyHours = 'Weekly hours is required';
    } else if (weeklyHours < 1) {
      errors.weeklyHours = 'Weekly hours must be at least 1';
    } else if (weeklyHours > 40) {
      errors.weeklyHours = 'Weekly hours cannot exceed 40';
    }

    if (formData.totalHours !== '' && formData.totalHours != null) {
      const totalHours = Number(formData.totalHours);
      if (Number.isNaN(totalHours) || totalHours < 1) {
        errors.totalHours = 'Total hours must be at least 1';
      } else if (totalHours > 2000) {
        errors.totalHours = 'Total hours cannot exceed 2000';
      } else if (!Number.isNaN(weeklyHours) && totalHours < weeklyHours) {
        errors.totalHours = 'Total hours cannot be less than weekly hours';
      }
    }

    const assignmentErrors = [];
    let hasAssignmentError = false;
    const seenClassTeacher = new Set();

    // Same class with different teachers is allowed; same class + same teacher is not.
    // Empty assignment list is allowed; partially filled rows are validated.
    formData.assignments.forEach((assignment, index) => {
      const error = {};
      const hasClass = Boolean(assignment.classId);
      const hasTeacher = Boolean(assignment.teacher);

      if (!hasClass && !hasTeacher) {
        assignmentErrors[index] = error;
        return;
      }

      if (!hasClass) {
        error.classId = "Class is required";
        hasAssignmentError = true;
      }

      if (!hasTeacher) {
        error.teacher = "Teacher is required";
        hasAssignmentError = true;
      }

      if (hasClass && hasTeacher) {
        const pairKey = `${Number(assignment.classId)}|${Number(assignment.teacher)}`;
        if (seenClassTeacher.has(pairKey)) {
          error.classId = "Duplicate assignment";
          error.teacher = "This class is already assigned with this teacher";
          hasAssignmentError = true;
        } else {
          seenClassTeacher.add(pairKey);
        }
      }

      assignmentErrors[index] = error;
    });

    if (hasAssignmentError) {
      errors.assignments = assignmentErrors;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const createSubjectMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: formData.name?.trim(),
        code: formData.code?.trim(),
        semester: getSemesterPayloadValue(),
        credits: Number(formData.credits ?? 1),
        weeklyHours: Number(formData.weeklyHours ?? 3),
        ...(formData.totalHours !== '' && formData.totalHours != null
          ? { totalHours: Number(formData.totalHours) }
          : {}),
        courseId: Number(formData.course),
        assignments: formData.assignments
          .filter((a) => a.classId && a.teacher)
          .map((a) => ({
            classId: Number(a.classId),
            teacherId: Number(a.teacher),
          })),
      };
      const res = await api.post("/subjects/create", payload, {
        headers: { "Content-Type": "application/json" },
      });
      return res.data;
    },
    onSuccess: (data) => {
      setCreateDialogOpen(false);
      setFormData({ ...EMPTY_SUBJECT_FORM, assignments: [{ classId: "", teacher: "" }] });
      setCourseClasses([]);
      setTeachers([]);
      setFieldErrors({});
      queryClient.invalidateQueries({ queryKey: ["subjects"] });
      setSnackbar({ open: true, message: data?.message || "Subject created successfully", severity: "success" });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error?.response?.data?.message || "Failed to create subject", severity: "error" });
    },
  });

  const handleCreateSubject = () => {
    if (!validateForm()) return;
    createSubjectMutation.mutate();
  };

  const updateSubjectMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        subjectId: Number(editingSubjectId),
        name: formData.name?.trim(),
        code: formData.code?.trim(),
        semester: getSemesterPayloadValue(),
        credits: Number(formData.credits ?? 1),
        weeklyHours: Number(formData.weeklyHours ?? 3),
        ...(formData.totalHours !== '' && formData.totalHours != null
          ? { totalHours: Number(formData.totalHours) }
          : { totalHours: null }),
        courseId: Number(formData.course),
        assignments: formData.assignments
          .filter((a) => a.classId && a.teacher)
          .map((a) => ({
            classId: Number(a.classId),
            teacherId: Number(a.teacher),
          })),
      };
      const res = await api.put("/subjects/update", payload, {
        headers: { "Content-Type": "application/json" },
      });
      return res.data;
    },
    onSuccess: (data) => {
      handleDialogClose();
      queryClient.invalidateQueries({ queryKey: ["subjects"] });
      setSnackbar({ open: true, message: data?.message || "Subject updated successfully", severity: "success" });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error?.response?.data?.message || "Failed to update subject", severity: "error" });
    },
  });

  const handleUpdateSubject = () => {
    if (!validateForm()) return;
    if (!editingSubjectId) return;
    updateSubjectMutation.mutate();
  };

  const handleDeleteSubject = (row) => {
    const id = row?.subjectId || row?.id;
    if (!id) return;
    setSubjectToDelete({ id, name: row?.name || "this subject" });
    setDeleteConfirmOpen(true);
  };

  const deleteSubjectMutation = useMutation({
    mutationFn: async (id) => {
      const res = await api.delete("/subjects/delete", { params: { id } });
      return res.data;
    },
    onSuccess: (data) => {
      setDeleteConfirmOpen(false);
      setSubjectToDelete(null);
      queryClient.invalidateQueries({ queryKey: ["subjects"], exact: false });
      setSnackbar({ open: true, message: data?.message || "Subject deleted successfully", severity: "success" });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error?.response?.data?.message || "Failed to delete subject", severity: "error" });
    },
  });

  const confirmDeleteSubject = () => {
    const id = subjectToDelete?.id;
    if (!id) return;
    deleteSubjectMutation.mutate(id);
  };

  const updateSubjectStatusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await api.put(
        "/subjects/subject-status-update",
        { status },
        { params: { id } }
      );
      return res.data;
    },
    onMutate: async ({ id }) => {
      setUpdatingStatusId(id);
    },
    onSuccess: (data) => {
      setUpdatingStatusId(null);
      setSnackbar({ open: true, message: data?.message || "Subject status updated", severity: "success" });
      queryClient.invalidateQueries({ queryKey: ["subjects"], exact: false });
    },
    onError: (error) => {
      setUpdatingStatusId(null);
      setSnackbar({ open: true, message: error?.response?.data?.message || "Failed to update status", severity: "error" });
    },
  });

  const handleToggleSubjectStatus = (row, newStatus) => {
    const id = row?.subjectId || row?.id;
    if (!id) return;
    if (row?.status && newStatus === false && row?.canDeactivate === false) return;
    updateSubjectStatusMutation.mutate({ id: Number(id), status: newStatus });
  };



  const columns = [
    {
      field: "name",
      headerName: "Subject",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const subjectName = params.row.name || "-";
        const subjectCode = params.row.code || "";
        return (
          <Tooltip title={subjectName} placement="top-start" enterDelay={400}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, width: "100%" }}>
              <Avatar sx={{ bgcolor: "secondary.light", width: 36, height: 36, flexShrink: 0 }}>
                <MenuBook />
              </Avatar>
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {subjectName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {subjectCode}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        );
      },
    },
    { field: "code", headerName: "Code", width: 110 },
    {
      field: "semester",
      headerName: "Semester",
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2">{params.value ?? "-"}</Typography>
      ),
    },
    {
      field: "credits",
      headerName: "Credits",
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">{params.value ?? "-"}</Typography>
      ),
    },
    {
      field: "weeklyHours",
      headerName: "Weekly Hrs",
      width: 105,
      renderCell: (params) => (
        <Typography variant="body2">{params.value ?? "-"}</Typography>
      ),
    },
    {
      field: "totalHours",
      headerName: "Total Hrs",
      width: 95,
      renderCell: (params) => (
        <Typography variant="body2">{params.value ?? "-"}</Typography>
      ),
    },
    {
      field: "remainingHours",
      headerName: "Remaining",
      width: 95,
      renderCell: (params) => (
        <Typography variant="body2">{params.value ?? "-"}</Typography>
      ),
    },
    {
      field: "className",
      headerName: "Classes",
      width: 200,
      renderCell: (params) => {
        const classes = String(params.value || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
        const firstClass = classes[0] || "-";
        const remainingCount = classes.length > 1 ? classes.length - 1 : 0;
        const tooltipText = classes.length ? classes.join(", ") : "-";

        return (
          <Tooltip title={tooltipText}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {firstClass}
              </Typography>
              {remainingCount > 0 && (
                <Chip
                  label={`+${remainingCount}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClassDialogOpen(classes);
                  }}
                  sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, flexShrink: 0, cursor: "pointer" }}
                />
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "teacher",
      headerName: "Teacher(s)",
      width: 250,
      cellClassName: 'teacher-cell',
      renderCell: (params) => {
        const teachers = params.row.teachers || [];
        const assignments = params.row.assignments || [];
        const mainTeacher = teachers[0] || (typeof params.value === 'string' ? params.value.split(',')[0] : '-') || '-';
        const remainingCount = teachers.length > 1 ? teachers.length - 1 : 0;

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: 'primary.main' }}>
              {mainTeacher[0] || "?"}
            </Avatar>
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {mainTeacher}
            </Typography>
            {remainingCount > 0 && (
              <Chip
                label={`+${remainingCount}`}
                size="small"
                color="primary"
                variant="outlined"
                onClick={(e) => handleTeacherPopoverOpen(e, assignments)}
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  ml: 0.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'primary.lighter' }
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={params.value ? "Active" : "Inactive"}
            color={params.value ? "success" : "default"}
            size="small"
            sx={{ minWidth: 80, fontWeight: "medium" }}
          />
          <PermissionGate permission="subject.update">
            <Tooltip title={getEntityStatusToggleTooltip(params.row, "subject")}>
              <span>
                <Switch
                  checked={Boolean(params.value)}
                  onChange={() => handleToggleSubjectStatus(params.row, !params.value)}
                  color="success"
                  size="small"
                  disabled={
                    !canToggleEntityStatus(params.row) ||
                    updatingStatusId === (params.row?.subjectId || params.row?.id)
                  }
                />
              </span>
            </Tooltip>
          </PermissionGate>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => openViewSubject(params.row)}>
              <Visibility />
            </IconButton>
          </Tooltip>
          <PermissionGate permission="subject.update">
            <Tooltip title="Edit Subject">
              <IconButton size="small" onClick={() => openEditSubject(params.row)}>
                <Edit />
              </IconButton>
            </Tooltip>
          </PermissionGate>
          {/* <Tooltip title="Assign to Class">
            <IconButton size="small" color="primary" onClick={() => openAssignDialog(params.row)}>
              <Add />
            </IconButton>
          </Tooltip> */}
          <PermissionGate permission="subject.delete">
            <Tooltip title="Delete Subject">
              <IconButton size="small" color="error" onClick={() => handleDeleteSubject(params.row)}>
                <Delete />
              </IconButton>
            </Tooltip>
          </PermissionGate>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Subject Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage subjects in your department
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <PermissionGate permission="subject.import">
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => setImportDialogOpen(true)}
            >
              Import
            </Button>
          </PermissionGate>
          <PermissionGate permission="subject.export">
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={rows.length === 0}
            >
              Export
            </Button>
          </PermissionGate>
          <PermissionGate permission="subject.create">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateSubject}
            >
              Add Subject
            </Button>
          </PermissionGate>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              placeholder="Search subjects..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <DepartmentModuleFilterSelect
              labelId="subjects-department-filter"
              value={departmentFilter}
              onChange={setDepartmentFilter}
              instituteId={effectiveInstituteId}
              showAllOption={showAllDepartmentsOption}
            />
            
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={courseFilterLoading}>
              <InputLabel>Course</InputLabel>
              <Select
                label="Course"
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
              >
                <MenuItem value="all">All Courses</MenuItem>
                {courseFilterOptions.map((c) => (
                  <MenuItem key={c.id || c.value} value={String(c.id || c.value)}>
                    {c.name || c.courseName || c.label || `Course ${c.id || c.value}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }} disabled={batchLoading}>
              <InputLabel>Batch</InputLabel>
              <Select
                label="Batch"
                value={academicYearFilter}
                onChange={(e) => setAcademicYearFilter(e.target.value)}
              >
                <MenuItem value="all">All Batches</MenuItem>
                {batchOptions.map((b) => (
                  <MenuItem key={b.id} value={b.academicYear}>
                    {b.academicYear}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Class</InputLabel>
              <Select
                label="Class"
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                disabled={classFilterLoading}
              >
                <MenuItem value="">All Classes</MenuItem>
                {classFilterOptions.map((cls) => {
                  const raw = cls?.class ?? cls?.Class ?? cls;
                  const id = cls?.id ?? cls?.classId ?? raw?.id ?? raw?.classId;
                  const name = raw?.name ?? cls?.name ?? raw?.className ?? cls?.className ?? `Class ${id}`;
                  const section = raw?.section ?? cls?.section ?? raw?.sectionName ?? cls?.sectionName ?? "";
                  const label = section ? `${name} - ${section}` : name;
                  return (
                    <MenuItem key={String(id)} value={String(id)}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {/* <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Course</InputLabel>
              <Select label="Course" defaultValue="">
                <MenuItem value="">All Courses</MenuItem>
                <MenuItem value="BTCS">B.Tech CS</MenuItem>
                <MenuItem value="BCA">BCA</MenuItem>
              </Select>
            </FormControl> */}
            {/* <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Semester</InputLabel>
              <Select label="Semester" defaultValue="">
                <MenuItem value="">All</MenuItem>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                  <MenuItem key={s} value={s}>
                    Sem {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
          </Box>

          {subjectsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {subjectsErrorObj?.response?.data?.message || "Failed to load subjects"}
            </Alert>
          )}
          <DataGrid
            rows={rows}
            columns={columns}
            loading={subjectsLoading || subjectsFetching}
            autoHeight
            disableRowSelectionOnClick
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={totalItems}
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            sx={{ border: "none", '& .teacher-cell': { pr: 2 } }}
          />
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <PersistentDialog
        open={createDialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {isEditMode ? 'Edit Subject' : 'Add New Subject'}
            <IconButton onClick={handleDialogClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="Subject Name *"
                value={formData.name}
                onChange={handleSubjectNameChange}
                onKeyDown={blockNumberKeyDown}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || "2-75 characters"}
                inputProps={{
                  minLength: 2,
                  maxLength: 75,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Code *"
                value={formData.code}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    code: e.target.value.slice(0, 75),
                  })
                }
                error={!!fieldErrors.code}
                helperText={fieldErrors.code || "2-75 characters"}
                inputProps={{
                  minLength: 2,
                  maxLength: 75,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ position: "relative" }}>
                <FormControl fullWidth error={!!fieldErrors.course}>
                  <InputLabel>Course *</InputLabel>
                  <Select
                    value={formData.course}
                    label="Course"
                    onChange={handleCourseChange}
                    disabled={coursesLoading}
                  >
                    <MenuItem value="">
                      <em>{coursesLoading ? "Loading courses..." : "Select Course"}</em>
                    </MenuItem>
                    {courseOptions.map((c) => (
                      <MenuItem key={c.id || c.value} value={c.id || c.value}>
                        {c.name || c.courseName || c.label || `Course ${c.id || c.value}`}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!fieldErrors.course && (
                    <FormHelperText>{fieldErrors.course}</FormHelperText>
                  )}
                </FormControl>
                {coursesLoading && (
                  <CircularProgress
                    size={18}
                    sx={{ position: "absolute", right: 36, top: 18, pointerEvents: "none" }}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ position: "relative" }}>
                <FormControl fullWidth error={!!fieldErrors.semester} disabled={semestersLoading}>
                  <InputLabel>Semester *</InputLabel>
                  <Select
                    value={formData.semester}
                    label="Semester *"
                    onChange={(e) =>
                      setFormData({ ...formData, semester: e.target.value })
                    }
                    renderValue={(v) => {
                      if (!v) return "";
                      const s = semestersForDropdown.find((sem) => String(sem.id) === String(v));
                      if (!s) return semestersLoading ? "Loading semesters..." : v;
                      const name = s.semesterName ?? s.name ?? v;
                      const yearPart = s.academicYear?.academicYear ?? s.academicYear;
                      return yearPart ? `${name} (${yearPart})` : name;
                    }}
                  >
                    <MenuItem value="">
                      <em>{semestersLoading ? "Loading semesters..." : "Select semester"}</em>
                    </MenuItem>
                    {semestersForDropdown.map((s) => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        {s.semesterName ?? s.name ?? s.id}
                        {s.academicYear?.academicYear || s.academicYear
                          ? ` (${s.academicYear?.academicYear ?? s.academicYear})`
                          : ""}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!fieldErrors.semester && (
                    <FormHelperText>{fieldErrors.semester}</FormHelperText>
                  )}
                </FormControl>
                {semestersLoading && (
                  <CircularProgress
                    size={18}
                    sx={{ position: "absolute", right: 36, top: 18, pointerEvents: "none" }}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Credits *"
                type="number"
                value={formData.credits}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty or any number between 1-20
                  if (value === '' || /^([1-9]|1[0-9]|20)$/.test(value)) {
                    setFormData({ ...formData, credits: value });
                  }
                }}
                onKeyPress={(e) => {
                  // Only allow digits
                  if (!/^\d$/.test(e.key)) {
                    e.preventDefault();
                    return;
                  }

                  const newValue = e.target.value + e.key;
                  const numValue = parseInt(newValue, 10);

                  // Allow single digits 1-9 or numbers 10-20
                  if (newValue.length > 2 ||
                    (newValue.length === 1 && numValue < 1) ||
                    (newValue.length === 2 && (numValue < 10 || numValue > 20))) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  // Allow paste but validate the pasted content is between 1-20
                  const paste = (e.clipboardData || window.clipboardData).getData('text');
                  const numValue = parseInt(paste, 10);
                  if (isNaN(numValue) || numValue < 1 || numValue > 20) {
                    e.preventDefault();
                  }
                }}
                error={!!fieldErrors.credits}
                helperText={fieldErrors.credits || "Enter a number between 3-20"}
                inputProps={{
                  min: 1,
                  max: 20,
                  maxLength: 2,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Weekly Hours *"
                type="number"
                value={formData.weeklyHours}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^([1-9]|[1-3][0-9]|40)$/.test(value)) {
                    setFormData({ ...formData, weeklyHours: value });
                  }
                }}
                error={!!fieldErrors.weeklyHours}
                helperText={fieldErrors.weeklyHours || "Lecture hours per week for timetable planning (1–40)"}
                inputProps={{ min: 1, max: 40 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Hours"
                type="number"
                value={formData.totalHours}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d{1,4}$/.test(value)) {
                    setFormData({ ...formData, totalHours: value });
                  }
                }}
                error={!!fieldErrors.totalHours}
                helperText={fieldErrors.totalHours || "Planned total hours for the term (optional)"}
                inputProps={{ min: 1, max: 2000 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  All Assign Teacher
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddAssignment}
                  variant="outlined"
                >
                  Add Assign Teacher
                </Button>
              </Box>

              {formData.assignments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mb: 2 }}>
                  No teacher-class assignments. Use &quot;Add Assign Teacher&quot; to add one, or save without assignments.
                </Typography>
              ) : null}

              {formData.assignments.map((assignment, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={5.5}>
                      <Box sx={{ position: "relative" }}>
                        <FormControl fullWidth error={!!(fieldErrors.assignments?.[index]?.classId)}>
                          <InputLabel>Class *</InputLabel>
                          <Select
                            value={assignment.classId}
                            label="Class"
                            onChange={(e) => handleAssignmentChange(index, 'classId', e.target.value)}
                            disabled={!formData.course}
                            displayEmpty
                            renderValue={renderClassSelectValue}
                          >
                            <MenuItem value="">
                              <em>
                                {!formData.course
                                  ? "Select course first"
                                  : courseClassesLoading
                                    ? "Loading classes..."
                                    : "Select Class"}
                              </em>
                            </MenuItem>
                            {courseClasses.map((c) => (
                              <MenuItem key={c.id ?? c.classId} value={String(c.id ?? c.classId)}>
                                {getClassOptionLabel(c)}
                              </MenuItem>
                            ))}
                          </Select>
                          {!!(fieldErrors.assignments?.[index]?.classId) && (
                            <FormHelperText>{fieldErrors.assignments[index].classId}</FormHelperText>
                          )}
                        </FormControl>
                        {formData.course && courseClassesLoading && (
                          <CircularProgress
                            size={18}
                            sx={{ position: "absolute", right: 36, top: 18, pointerEvents: "none" }}
                          />
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={5.5}>
                      <Box sx={{ position: "relative" }}>
                        <FormControl fullWidth error={!!(fieldErrors.assignments?.[index]?.teacher)}>
                          <InputLabel>Teacher *</InputLabel>
                          <Select
                            value={assignment.teacher}
                            label="Teacher"
                            onChange={(e) => handleAssignmentChange(index, 'teacher', e.target.value)}
                            disabled={!formData.course}
                            displayEmpty
                            renderValue={renderTeacherSelectValue}
                            MenuProps={{
                              autoFocus: false,
                              PaperProps: { style: { maxHeight: 360 } },
                            }}
                            onClose={() => setTeacherSearch("")}
                          >
                            {/* In-menu search box. Keep key events local so typing does not
                                trigger Select's type-ahead or close the menu. */}
                            <Box
                              sx={{ px: 1.5, py: 1, position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1 }}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <TextField
                                size="small"
                                fullWidth
                                autoFocus
                                placeholder="Search teacher by name"
                                value={teacherSearch}
                                onChange={(e) => setTeacherSearch(e.target.value)}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Search fontSize="small" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Box>
                            <MenuItem value="">
                              <em>
                                {!formData.course
                                  ? "Select course first"
                                  : teachersLoading
                                    ? "Loading teachers..."
                                    : "Select Teacher"}
                              </em>
                            </MenuItem>
                            {filteredTeachers.map((t) => {
                              const name = getTeacherDisplayName(t) || `Teacher ${t.id}`;
                              return (
                                <MenuItem key={t.id || t.teacherId} value={String(t.id || t.teacherId)}>
                                  {name}
                                </MenuItem>
                              );
                            })}
                            {formData.course && !teachersLoading && filteredTeachers.length === 0 && (
                              <MenuItem disabled>
                                {teacherSearch.trim() ? "No matching teachers" : "No teachers available"}
                              </MenuItem>
                            )}
                          </Select>
                          {!!(fieldErrors.assignments?.[index]?.teacher) && (
                            <FormHelperText>{fieldErrors.assignments[index].teacher}</FormHelperText>
                          )}
                        </FormControl>
                        {formData.course && teachersLoading && (
                          <CircularProgress
                            size={18}
                            sx={{ position: "absolute", right: 36, top: 18, pointerEvents: "none" }}
                          />
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'center', pt: { xs: 0, sm: 2 } }}>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveAssignment(index)}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={isEditMode ? handleUpdateSubject : handleCreateSubject}
            disabled={isEditMode ? updateSubjectMutation.isPending : createSubjectMutation.isPending}
            startIcon={
              (isEditMode ? updateSubjectMutation.isPending : createSubjectMutation.isPending)
                ? <CircularProgress size={18} color="inherit" />
                : null
            }
          >
            {isEditMode
              ? (updateSubjectMutation.isPending ? 'Updating...' : 'Update Subject')
              : (createSubjectMutation.isPending ? 'Creating...' : 'Create Subject')}
          </Button>
        </DialogActions>
      </PersistentDialog>

      {/* View Subject Dialog */}
      <PersistentDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            Subject Details
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: 'secondary.light' }}>
                  <MenuBook />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {viewSubject?.name || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Code: {viewSubject?.code || '-'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 1 }}>All Assign Teacher</Typography>
              {viewSubject?.assignments?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {viewSubject.assignments.map((assignment, index) => {
                    const tName =
                      assignment.teacherName ||
                      assignment.teacher?.fullName ||
                      assignment.Teacher?.user?.fullName ||
                      assignment.Teacher?.fullName ||
                      "";
                    const classId =
                      assignment.classId ??
                      assignment.class_id ??
                      assignment.class?.id ??
                      assignment.Class?.id ??
                      null;
                    const sectionFromApi =
                      assignment.section ??
                      assignment.sectionName ??
                      assignment.section_name ??
                      assignment.classDetails?.section ??
                      assignment.class?.section ??
                      assignment.Class?.section ??
                      "";
                    const sectionFallback =
                      sectionFromApi ||
                      (classId != null ? classSectionMap[String(classId)] : "") ||
                      "";
                    const classLabel =
                      assignment.classDisplayLabel ||
                      getClassDisplayLabel({
                        name:
                          assignment.className ||
                          assignment.class?.name ||
                          assignment.Class?.name ||
                          "",
                        section: sectionFallback,
                      });
                    return (
                      <Paper key={index} variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                        <Typography variant="body2"><strong>Class:</strong> {classLabel || "—"}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          <strong>Teacher:</strong> {tName || "—"}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              ) : viewSubject?.className || viewSubject?.teacher ? (
                <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                  <Typography variant="body2"><strong>Class:</strong> {viewSubject.className || "—"}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    <strong>Teacher:</strong> {viewSubject.teacher || viewSubject.teacherName || "—"}
                  </Typography>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">No assignments found</Typography>
              )}
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Semester</Typography>
              <Typography variant="body2">{viewSubject?.semester ?? '-'}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Credits</Typography>
              <Typography variant="body2">{viewSubject?.credits ?? '-'}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Weekly Hours</Typography>
              <Typography variant="body2">
                {viewSubject?.weeklyHours ?? viewSubject?.hoursPerWeek ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Total Hours</Typography>
              <Typography variant="body2">{viewSubject?.totalHours ?? '-'}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Completed Hours</Typography>
              <Typography variant="body2">{viewSubject?.completedHours ?? 0}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Remaining Hours</Typography>
              <Typography variant="body2">{viewSubject?.remainingHours ?? '-'}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box>
                <Chip size="small" label={(viewSubject?.status ? 'Active' : 'Inactive')} color={viewSubject?.status ? 'success' : 'default'} />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </PersistentDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirm Dialog */}
      <PersistentDialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Subject</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete {subjectToDelete?.name || 'this subject'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteSubject}
            disabled={deleteSubjectMutation.isPending}
          >
            {deleteSubjectMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </PersistentDialog>

      {/* Import Wizard */}
      <DataImportWizard
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        entityType="subjects"
        instituteId={currentUser?.instituteId}
        onImportComplete={() => {
          // Invalidate subjects query to refresh the list
          queryClient.invalidateQueries({ queryKey: ["subjects"] });
        }}
      />

      {/* Assign Subject to Class Dialog */}
      <PersistentDialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manage Subject Assignments</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Manage assignments for "{selectedSubject?.name}":
          </Typography>

          {/* Existing Assignments */}
          {assignmentsLoading ? (
            <CircularProgress size={24} />
          ) : existingAssignments.length > 0 ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Current Assignments</Typography>
              {existingAssignments.map((assignment) => (
                <Card key={assignment.id} sx={{ mb: 1, p: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2">
                        <strong>Class:</strong> {getClassDisplayLabel(assignment.class)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Hours:</strong> {assignment.hoursPerWeek} | <strong>Compulsory:</strong> {assignment.isCompulsory ? 'Yes' : 'No'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Academic Year:</strong> {assignment.academicYear}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton size="small" onClick={() => handleEditAssignment(assignment)}>
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteAssignment(assignment.id)}>
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
              No assignments found for this subject.
            </Typography>
          )}

          {/* Add/Edit Assignment Form */}
          <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {editingAssignment ? 'Edit Assignment' : 'Add New Assignment'}
            </Typography>
            {classesLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Class</InputLabel>
                    <Select
                      label="Class"
                      value={editingAssignment?.classId || ''}
                      onChange={(e) => setEditingAssignment(prev => ({ ...prev, classId: e.target.value }))}
                    >
                      {classes.map((cls) => (
                        <MenuItem key={cls.id} value={cls.id}>
                          {cls.name} - {cls.section}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hours Per Week"
                    type="number"
                    value={editingAssignment?.hoursPerWeek || 3}
                    onChange={(e) => setEditingAssignment(prev => ({ ...prev, hoursPerWeek: parseInt(e.target.value) }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editingAssignment?.isCompulsory !== undefined ? editingAssignment.isCompulsory : true}
                        onChange={(e) => setEditingAssignment(prev => ({ ...prev, isCompulsory: e.target.checked }))}
                      />
                    }
                    label="Is Compulsory"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Academic Year"
                    value={editingAssignment?.academicYear || `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`}
                    onChange={(e) => setEditingAssignment(prev => ({ ...prev, academicYear: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleSaveAssignment(editingAssignment || {})}
                      disabled={!editingAssignment?.classId}
                    >
                      {editingAssignment?.id ? 'Update Assignment' : 'Add Assignment'}
                    </Button>
                    {editingAssignment && (
                      <Button variant="outlined" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Close</Button>
        </DialogActions>
      </PersistentDialog>
      {/* Teacher Popover */}
      <PersistentDialog
        open={Boolean(teacherPopoverAnchor)}
        onClose={handleTeacherPopoverClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">All Assign Teacher</Typography>
            <IconButton size="small" onClick={handleTeacherPopoverClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {teacherPopoverData.map((a, i) => (
              <Paper
                key={i}
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: 'secondary.main' }}>
                  {a.teacherName ? a.teacherName[0] : '?'}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {a.teacherName || 'Unknown Teacher'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`Class: ${
                      a.classDisplayLabel ||
                      getClassDisplayLabel({
                        name: a.className,
                        section:
                          a.section ??
                          a.classDetails?.section ??
                          a.class?.section ??
                          (a.classId != null ? classSectionMap[String(a.classId)] : ""),
                      }) ||
                      "Unknown Class"
                    }`}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </DialogContent>
      </PersistentDialog>
      <PersistentDialog open={classDialogOpen} onClose={handleClassDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">All Classes</Typography>
            <IconButton size="small" onClick={handleClassDialogClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
            {classDialogData.map((className, i) => (
              <Paper key={`${className}-${i}`} variant="outlined" sx={{ p: 1.5, bgcolor: "grey.50" }}>
                <Typography variant="body2" fontWeight={600}>
                  {className}
                </Typography>
              </Paper>
            ))}
          </Box>
        </DialogContent>
      </PersistentDialog>
    </Box>
  );
};

export default Subjects;
